import { VIOLATION_REVIEW_NOTE_MAX_LENGTH } from '@aysar/contracts';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { Skeleton } from '@/shared/components/feedback/Skeleton/Skeleton';
import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';
import { Label } from '@/shared/components/ui/Label/Label';
import { Textarea } from '@/shared/components/ui/Textarea/Textarea';

import { DecisionRefusal } from '../DecisionRefusal/DecisionRefusal';
import { ViolationConsequence } from '../ViolationConsequence/ViolationConsequence';

import { useEnforceViolationModalLogic } from './useEnforceViolationModalLogic';

/**
 * Confirm a violation whose rule is already known.
 *
 * It exists as its **own** dialog rather than a branch of one, because
 * `POST /enforce` reads `violationTypeId` only for a row the classifier could
 * not judge: this dialog never sends it, `ClassifyAndEnforceModal` always does,
 * and the queue chooses between them from the row. That is what makes the 422
 * unreachable rather than merely unlikely.
 *
 * **There is no action picker here.** What runs is the rule's *configured*
 * consequence (N20), read live so the promise on screen is the one the api will
 * keep — and `enforce.noOverride` says so, because a control that is missing
 * without explanation reads as one that is broken.
 */
export function EnforceViolationModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('violations');
  const {
    violation,
    isLoading,
    reload,
    violationTypeId,
    note,
    setNote,
    canSubmit,
    isSaving,
    submit,
    error,
    isConflict,
    isGone,
  } = useEnforceViolationModalLogic(modalData, onCancel, { isClassifying: false });

  /**
   * A colleague decided first: re-read the shared cache entry and close.
   *
   * Both dialogs subscribe to the same `violation(id)` entry, so one refetch
   * leaves the review dialog underneath showing the verdict that was actually
   * recorded — which is the thing this moderator now needs to see, and it is
   * behind this confirmation rather than in it.
   */
  const rereadAndClose = () => {
    reload();
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t('enforce.title')}</DialogTitle>
          <DialogDescription>{t('enforce.subtitle')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {isLoading ? (
            <div className="flex flex-col gap-md" aria-label={t('review.loading')}>
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : violation === null ? null : (
            <div className="flex flex-col gap-lg">
              <ViolationConsequence violationTypeId={violationTypeId} label={t('enforce.willDo')} />

              <p className="flex items-start gap-sm text-body text-muted-foreground">
                <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t('enforce.noOverride')}</span>
              </p>

              <div className="flex flex-col gap-xs">
                <Label htmlFor="violation-enforce-note">{t('enforce.note')}</Label>
                <Textarea
                  id="violation-enforce-note"
                  aria-describedby="violation-enforce-note-hint"
                  value={note}
                  maxLength={VIOLATION_REVIEW_NOTE_MAX_LENGTH}
                  onChange={(event) => {
                    setNote(event.target.value);
                  }}
                />
                <p id="violation-enforce-note-hint" className="text-caption text-muted-foreground">
                  {t('enforce.noteHint')}
                </p>
              </div>

              <DecisionRefusal
                message={error}
                isConflict={isConflict}
                isGone={isGone}
                onReload={rereadAndClose}
                onClose={onCancel}
              />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t('enforce.cancel')}
          </Button>
          <Button
            isLoading={isSaving}
            disabled={!canSubmit}
            onClick={() => {
              void submit();
            }}
          >
            {t('enforce.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
